import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import Title from "antd/es/typography/Title";
import queryClient from "api/queryClient";
import { useGetOfferQuery } from "api/TenantSlug/Offer/hooks";
import {
    useDestroyOfferLinkMutation,
    useGetOfferLinksQuery,
    useStoreOfferLinkMutation,
    useUpdateOfferLinkMutation,
} from "api/TenantSlug/OfferLink/hooks";
import classNames from "classnames";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { FC, useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { RoleEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { OfferLinkEditFormData, LinkFormData, schema } from "./formSchema.ts";

import OfferData = App.Data.OfferData;
import OfferLinkDataOptional = App.Data.OfferLinkDataOptional;

const EMPTY_LINK: LinkFormData = {
    id: undefined,
    title: "",
    url: "",
};

interface OfferLinkEditFormContentProps {
    offer: OfferData;
    offerLinks: OfferLinkDataOptional[];
    deletedIds: string[];
    setDeletedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const OfferLinkEditFormContent: FC<OfferLinkEditFormContentProps> = ({
    offer,
    offerLinks,
    deletedIds,
    setDeletedIds,
}) => {
    const { t } = usePaldockTranslation("forms.offer-link-edit");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tOffers } = usePaldockTranslation("pages.offer.edit");
    const generalFormUtils = useGeneralForm<OfferLinkEditFormData>();
    const { control, reset, getValues } = generalFormUtils;

    const { fields, append, remove } = useFieldArray({
        control,
        name: "links",
    });

    const userUtils = useUser();
    const showDetailButton = userUtils
        ?.getActiveTenant()
        ?.userRoles?.some((role) => [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name));
    const userId = userUtils?.getAuthenticatedUser()?.id;

    // Reset form when offerLinks change
    useEffect(() => {
        if (offerLinks && userId) {
            const userLinks = offerLinks
                .filter((offerLink) => offerLink.owner_id === userId)
                .map((offerLink) => ({
                    id: offerLink.id,
                    title: offerLink.title ?? "",
                    url: offerLink.landing_url ?? "",
                }));
            reset({ links: userLinks });
            // Clear deleted IDs when links reload
            setDeletedIds([]);
        }
    }, [offerLinks, userId, reset, setDeletedIds]);

    const handleAddLink = () => {
        append(EMPTY_LINK);
    };

    const handleRemoveLink = (index: number) => {
        // Get the actual link ID from form values (not field.id which is react-hook-form's internal key)
        const linkId = getValues(`links.${index}.id`);

        if (linkId) {
            // Existing link - mark as deleted (will be deleted on save)
            setDeletedIds((prev) => [...prev, linkId]);
        } else {
            // New link (not saved yet) - just remove from form
            remove(index);
        }
    };

    return (
        <SectionContentWrapper withBackground>
            <div className="mb-4 flex flex-row justify-between">
                <Title level={3} className="!mb-0">
                    {t("sections.links")}
                </Title>
                <div className="flex gap-2">
                    {showDetailButton && offer?.id && (
                        <TenantLink to="/offer/detail/$offerId/link" params={{ offerId: offer.id }}>
                            <Button>{tOffers("get_link")}</Button>
                        </TenantLink>
                    )}
                    <Button onClick={handleAddLink} icon={<PlusOutlined />} type="primary">
                        {tC("add")}
                    </Button>
                </div>
            </div>
            {/* Header row */}
            {fields.length > 0 && (
                <div className="flex items-center gap-x-4 w-full mb-2">
                    <div className="w-1/4">
                        <span className="font-medium">{tFields("title.label")}</span>
                    </div>
                    <div className="flex-1">
                        <span className="font-medium">{t("fields.url.label")}</span>
                    </div>
                    <div className="w-6" /> {/* Spacer for delete button */}
                </div>
            )}
            {/* Data rows */}
            {fields.map((field, index) => {
                // Get actual link ID from form values (field.id is react-hook-form's internal key)
                const actualLinkId = getValues(`links.${index}.id`);
                const isDeleted = actualLinkId ? deletedIds.includes(actualLinkId) : false;

                return (
                    <div
                        key={field.id}
                        className={classNames("flex items-center gap-x-4 w-full mb-3", isDeleted && "opacity-50")}
                    >
                        <div className="w-1/4">
                            <FormItem
                                control={control}
                                name={`links.${index}.title`}
                                required={!isDeleted}
                                className={"!mb-0"}
                                disabled={isDeleted}
                            >
                                <Input disabled={isDeleted} />
                            </FormItem>
                        </div>

                        <div className="flex flex-row gap-x-4 flex-1 items-center">
                            <FormItem
                                control={control}
                                name={`links.${index}.url`}
                                className="grow !mb-0"
                                required={!isDeleted}
                                disabled={isDeleted}
                            >
                                <Input placeholder={t("fields.url.placeholder")} disabled={isDeleted} />
                            </FormItem>

                            <MinusCircleOutlined
                                className={classNames(
                                    "dynamic-delete-button cursor-pointer",
                                    isDeleted ? "text-gray-400 cursor-not-allowed" : "text-red-500 hover:text-red-700"
                                )}
                                onClick={() => !isDeleted && handleRemoveLink(index)}
                            />
                        </div>
                    </div>
                );
            })}
        </SectionContentWrapper>
    );
};

interface OfferLinkEditFormProps {
    offerId: string;
}

export const OfferLinkEditForm: FC<OfferLinkEditFormProps> = ({ offerId }) => {
    const { data: offerData, isLoading: isLoadingOffer, isError: isOfferError } = useGetOfferQuery(offerId, {});
    const offer = offerData?.offer;
    const { t } = usePaldockTranslation("forms.offer-link-edit");
    const [deletedIds, setDeletedIds] = useState<string[]>([]);

    const userUtils = useUser();
    const userId = userUtils?.getAuthenticatedUser()?.id;

    // Fetch fresh offer links data
    const {
        data: offerLinksData,
        isLoading: isLoadingLinks,
        isError: isLinksError,
    } = useGetOfferLinksQuery(
        {
            filter: {
                filter: { offer_id: offer?.id },
                fields: { offer_links: "title,landing_url,owner_id" },
            },
        },
        { enabled: !!offer?.id }
    );

    const offerLinks = offerLinksData?.offerLinks ?? [];

    const updateOfferLinkMutation = useUpdateOfferLinkMutation();
    const storeOfferLinkMutation = useStoreOfferLinkMutation();
    const destroyOfferLinkMutation = useDestroyOfferLinkMutation();

    const defaultValues: OfferLinkEditFormData = {
        links:
            offerLinks
                ?.filter((offerLink) => offerLink.owner_id === userId)
                .map((offerLink) => ({
                    id: offerLink.id,
                    title: offerLink.title ?? "",
                    url: offerLink.landing_url ?? "",
                })) ?? [],
    };

    const handleSubmit = async (values: OfferLinkEditFormData) => {
        const existingLinkIds = offerLinks?.map((l) => l.id) ?? [];

        try {
            // 1. Delete marked links
            const deletePromises = deletedIds.map((id) => destroyOfferLinkMutation.mutateAsync({ id }));

            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
            }

            // 2. Update and create links (excluding deleted ones)
            const linksToProcess = values.links.filter((link) => !link.id || !deletedIds.includes(link.id));

            const mutationPromises = linksToProcess.map((link) => {
                if (link.id && existingLinkIds.includes(link.id)) {
                    // Update existing link
                    return updateOfferLinkMutation.mutateAsync({
                        offerLinkId: link.id,
                        request: {
                            title: link.title,
                            landing_url: link.url,
                        },
                    });
                } else {
                    if (!offer?.id) {
                        throw new Error("Cannot create link: offer ID is missing");
                    }
                    // Create new link
                    return storeOfferLinkMutation.mutateAsync({
                        title: link.title,
                        landing_url: link.url,
                        owner_id: userId,
                        offer_id: offer.id,
                    });
                }
            });

            if (mutationPromises.length > 0) {
                await Promise.all(mutationPromises);
            }

            toast.success(t("messages.update_success"));

            // Reset deleted IDs after successful save
            setDeletedIds([]);
        } catch (error) {
            console.error("Error saving links:", error);
            toast.error(t("messages.update_error"));
        } finally {
            // Always resync queries — partial failures may have mutated server state
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["getOffer", offerId] }),
                queryClient.invalidateQueries({ queryKey: ["getOfferLinks"] }),
            ]);
        }
    };

    if (isLoadingOffer || isLoadingLinks || isOfferError || isLinksError) {
        return (
            <QueryState
                isLoading={isLoadingOffer || isLoadingLinks}
                isError={isOfferError || isLinksError}
                errorKey="errors.loading_offer"
            />
        );
    }

    if (!offer) {
        return null;
    }

    return (
        <GeneralFormProvider<OfferLinkEditFormData>
            key={`${offer.id}-${offerLinks.length}`}
            schema={schema}
            defaultValues={defaultValues}
            id={FormId.OfferLinkEditForm}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
            requiredMark={false}
        >
            <OfferLinkEditFormContent
                offer={offer}
                offerLinks={offerLinks}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
            />
        </GeneralFormProvider>
    );
};
